import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  Tooltip,
  Card,
  Grid,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Menu,
} from '@mui/material';
import {
  Delete,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  ThumbUp,
  Add,
  WaterDrop,
  Visibility,
  Edit,
  FileDownload,
  Close,
  AttachFile,
  Business,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { usePermissions } from '../../hooks/usePermissions';
import { ApprovalDialog } from '../shared/ApprovalDialog';
import { useApp } from '../../contexts/AuthContext';
import { FileUploadZone, UploadedFile } from '../shared/FileUploadZone';
import {
  waterService,
  waterAttachmentService,
  WaterEntry,
  WaterCategory,
  WaterSource,
  WaterQualityType,
  DischargeDestination,
  DischargeTreatmentLevel,
  ThirdPartyWaterType,
  RecyclingType,
  ReusePurpose,
  GRI303Summary,
  IntensityMetricsResponse,
  WaterAttachment,
} from '../../services';

const WATER_SOURCES: Record<string, string[]> = {
  Withdrawal: ['Municipal Water', 'Groundwater', 'Surface Water', 'Rainwater', 'Seawater', 'Third-party Water', 'Produced/Process Water'],
  Discharge: ['Municipal Water', 'Surface Water', 'Groundwater', 'Third-party Water', 'Seawater'],
  Consumption: ['Municipal Water', 'Groundwater', 'Surface Water', 'Recycled/Reused'],
  Recycled: ['Recycled/Reused', 'Municipal Water'],
};

const DISCHARGE_DESTINATIONS = [
  { value: 'surface_water', label: 'Surface Water' },
  { value: 'groundwater', label: 'Groundwater' },
  { value: 'seawater', label: 'Seawater' },
  { value: 'third_party', label: 'Third-party Treatment' },
  { value: 'on_site_treatment', label: 'On-site Treatment' },
];

const TREATMENT_LEVELS = [
  { value: 'none', label: 'No Treatment' },
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'tertiary', label: 'Tertiary' },
];

const THIRD_PARTY_TYPES = [
  { value: 'municipal_supply', label: 'Municipal Supply' },
  { value: 'wastewater_from_org', label: 'Wastewater from Other Organization' },
];

// CDP W1.2d: Water Recycling Options
const RECYCLING_TYPES = [
  { value: 'internal', label: 'Internal (Reused within facility)' },
  { value: 'external', label: 'External (Sent to third party)' },
];

const REUSE_PURPOSES = [
  { value: 'cooling', label: 'Cooling' },
  { value: 'process', label: 'Process Water' },
  { value: 'irrigation', label: 'Irrigation' },
  { value: 'sanitation', label: 'Sanitation' },
  { value: 'other', label: 'Other' },
];

export const WaterDataCollection: React.FC = () => {
  const { user, selectedPeriod, facilities } = useApp();
  const permissions = usePermissions();

  // State
  const [waterData, setWaterData] = useState<WaterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<WaterEntry | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WaterEntry | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Withdrawal, 1: Discharge, 2: Consumption

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [facilityFilter, setFacilityFilter] = useState<string>('all');
  const [chartFacilityFilter, setChartFacilityFilter] = useState<string>('all');

  // Summary stats
  const [summary, setSummary] = useState({
    totalWithdrawal: 0,
    totalDischarge: 0,
    totalConsumption: 0,
    totalRecycled: 0,
    approvedCount: 0,
    pendingCount: 0,
  });

  // GRI 303 and CDP metrics state
  const [gri303Data, setGri303Data] = useState<GRI303Summary | null>(null);
  const [intensityData, setIntensityData] = useState<IntensityMetricsResponse | null>(null);
  const [recyclingMetricType, setRecyclingMetricType] = useState<'rate' | 'ratio'>('rate');
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Form data for manual entry
  const [formData, setFormData] = useState({
    category: 'Withdrawal',
    source: '',
    amount: '',
    unit: 'm³',
    facility_id: '',
    reporting_period: '',
    notes: '',
    stressed_area: false,
    // GRI 303 fields
    water_quality_type: 'freshwater' as WaterQualityType,
    tds_mg_per_liter: '',
    discharge_destination: '' as DischargeDestination | '',
    discharge_treatment_level: '' as DischargeTreatmentLevel | '',
    // CDP Water Security fields
    third_party_type: '' as ThirdPartyWaterType | '',
    is_produced_water: false,
    // CDP W1.2d: Water Recycling fields
    recycling_type: '' as RecyclingType | '',
    reuse_purpose: '' as ReusePurpose | '',
    input_volume: '',
    output_volume: '',
  });

  // Attachment state for evidence files
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);

  // Update form's default reporting period when dialog opens
  useEffect(() => {
    if (entryDialogOpen) {
      const year = selectedPeriod?.year || new Date().getFullYear();
      const month = String(new Date().getMonth() + 1).padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        reporting_period: `${year}-${month}`
      }));
    }
  }, [entryDialogOpen, selectedPeriod?.year]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await waterService.listEntries({
        period: selectedPeriod?.year?.toString(),
      });
      setWaterData(response.entries || []);

      // Calculate totals
      const withdrawal = (response.entries || [])
        .filter((e: WaterEntry) => e.category === 'Withdrawal')
        .reduce((sum: number, e: WaterEntry) => sum + e.amount, 0);
      const discharge = (response.entries || [])
        .filter((e: WaterEntry) => e.category === 'Discharge')
        .reduce((sum: number, e: WaterEntry) => sum + e.amount, 0);
      const recycled = (response.entries || [])
        .filter((e: WaterEntry) => e.category === 'Recycled')
        .reduce((sum: number, e: WaterEntry) => sum + e.amount, 0);

      setSummary({
        totalWithdrawal: response.total_withdrawal || withdrawal,
        totalDischarge: response.total_discharge || discharge,
        totalConsumption: (response.total_withdrawal || withdrawal) - (response.total_discharge || discharge),
        totalRecycled: response.total_recycled || recycled,
        approvedCount: response.approved_count || 0,
        pendingCount: response.pending_count || 0,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load water data');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod?.year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch GRI 303 and CDP metrics
  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const [gri, intensity] = await Promise.all([
        waterService.getGRI303Summary(selectedPeriod?.year?.toString()),
        waterService.getIntensityMetrics(selectedPeriod?.year?.toString()),
      ]);
      setGri303Data(gri);
      setIntensityData(intensity);
    } catch (err: any) {
      console.error('Failed to load metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, [selectedPeriod?.year]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Reset page when switching tabs
  useEffect(() => {
    setPage(0);
    setFacilityFilter('all');
  }, [activeTab]);

  // Facility filter options - derived from available facilities
  const facilityOptions = [
    { value: 'all', label: 'All Facilities' },
    ...facilities.map(f => ({ value: f.id, label: f.name }))
  ];

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { icon: React.ReactElement; color: string; bg: string; label: string }> = {
      approved: { icon: <CheckCircle sx={{ fontSize: 16 }} />, color: '#059669', bg: '#ecfdf5', label: 'Approved' },
      submitted: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      pending: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      draft: { icon: <Edit sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Draft' },
      rejected: { icon: <ErrorIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Rejected' },
    };
    return configs[status] || configs.draft;
  };

  const handleApprove = async (entryId: string, comment?: string) => {
    try {
      await waterService.approveEntry(entryId, comment);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to approve entry');
    }
  };

  const handleReject = async (entryId: string, reason: string) => {
    try {
      await waterService.rejectEntry(entryId, reason);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to reject entry');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await waterService.deleteEntry(entryId);
        fetchData();
      } catch (err: any) {
        setError(err.message || 'Failed to delete entry');
      }
    }
  };

  // Handle file upload for evidence attachments
  const handleFileUpload = async (files: File[]): Promise<UploadedFile[]> => {
    const [year, month] = formData.reporting_period.split('-').map(Number);
    const uploaded = await waterAttachmentService.uploadFiles(files, year, month);
    return uploaded.map((a: WaterAttachment) => ({
      id: a.id,
      original_filename: a.original_filename,
      file_size_bytes: a.file_size_bytes,
      file_type: a.file_type,
      uploaded_by: a.uploaded_by,
      uploaded_at: a.uploaded_at,
    }));
  };

  // Handle file removal
  const handleFileRemove = async (fileId: string): Promise<void> => {
    await waterAttachmentService.deleteAttachment(fileId);
  };

  // Export menu state (Admin only)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExportMenuAnchor(null);
    if (tabFilteredData.length === 0) return;

    setExporting(true);
    try {
      const period = selectedPeriod ? `${selectedPeriod.year}` : undefined;
      await waterAttachmentService.exportData(format, period);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleEdit = async (entry: WaterEntry) => {
    // Extract year and month from reporting_period
    const [year, month] = entry.reporting_period?.split('-') || [new Date().getFullYear().toString(), '01'];

    setEditingEntry(entry);
    setEditMode(true);
    setFormData({
      category: entry.category,
      // Don't load source for Recycled/Discharge categories (not needed)
      source: entry.category === 'Recycled' || entry.category === 'Discharge' ? '' : entry.source,
      amount: entry.amount.toString(),
      unit: entry.unit || 'm³',
      facility_id: entry.facility_id,
      reporting_period: `${year}-${month}`,
      notes: '', // Notes not in response
      stressed_area: entry.stressed_area,
      water_quality_type: (entry.water_quality_type as WaterQualityType) || 'freshwater',
      tds_mg_per_liter: entry.tds_mg_per_liter?.toString() || '',
      discharge_destination: (entry.discharge_destination as DischargeDestination) || '',
      discharge_treatment_level: (entry.discharge_treatment_level as DischargeTreatmentLevel) || '',
      third_party_type: (entry.third_party_type as ThirdPartyWaterType) || '',
      is_produced_water: entry.is_produced_water || false,
      recycling_type: (entry.recycling_type as RecyclingType) || '',
      reuse_purpose: (entry.reuse_purpose as ReusePurpose) || '',
      input_volume: entry.input_volume?.toString() || '',
      output_volume: entry.output_volume?.toString() || '',
    });

    // Load existing attachments for the entry (admin only)
    try {
      const existingAttachments = await waterAttachmentService.listEntryAttachments(entry.id);
      setAttachments(existingAttachments.map((a: WaterAttachment) => ({
        id: a.id,
        original_filename: a.original_filename,
        file_size_bytes: a.file_size_bytes,
        file_type: a.file_type,
        uploaded_by: a.uploaded_by,
        uploaded_at: a.uploaded_at,
        water_entry_id: a.water_entry_id,
      })));
    } catch {
      // Silently fail - user may not have permission to view attachments
      setAttachments([]);
    }

    setEntryDialogOpen(true);
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;

    setSubmitting(true);
    try {
      const [year, month] = formData.reporting_period.split('-').map(Number);

      // For Recycled category, use output_volume as amount
      const amount = formData.category === 'Recycled'
        ? parseFloat(formData.output_volume) || 0
        : parseFloat(formData.amount) || 0;

      await waterService.updateEntry(editingEntry.id, {
        category: formData.category as WaterCategory,
        source: formData.source ? formData.source as WaterSource : undefined,
        amount,
        unit: formData.category === 'Recycled' ? 'm³' : formData.unit as 'm³' | 'ML' | 'kL',
        facility_id: formData.facility_id,
        year,
        month,
        reporting_period: formData.reporting_period,
        stressed_area: formData.stressed_area,
        notes: formData.notes || undefined,
        water_quality_type: formData.water_quality_type,
        tds_mg_per_liter: formData.tds_mg_per_liter ? parseFloat(formData.tds_mg_per_liter) : undefined,
        discharge_destination: formData.discharge_destination || undefined,
        discharge_treatment_level: formData.discharge_treatment_level || undefined,
        third_party_type: formData.third_party_type || undefined,
        is_produced_water: formData.is_produced_water,
        recycling_type: formData.recycling_type || undefined,
        reuse_purpose: formData.reuse_purpose || undefined,
        input_volume: formData.input_volume ? parseFloat(formData.input_volume) : undefined,
        output_volume: formData.output_volume ? parseFloat(formData.output_volume) : undefined,
      });

      // Link any new attachments to the entry
      if (attachments.length > 0) {
        await Promise.all(
          attachments
            .filter(a => !a.water_entry_id) // Only link unlinked attachments
            .map(attachment =>
              waterAttachmentService.linkAttachmentToEntry(attachment.id, editingEntry.id)
            )
        );
      }

      // Close and reset
      setEntryDialogOpen(false);
      setEditMode(false);
      setEditingEntry(null);
      resetFormData();
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update entry');
    } finally {
      setSubmitting(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      category: 'Withdrawal',
      source: '',
      amount: '',
      unit: 'm³',
      facility_id: '',
      reporting_period: `${selectedPeriod?.year || new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
      notes: '',
      stressed_area: false,
      water_quality_type: 'freshwater',
      tds_mg_per_liter: '',
      discharge_destination: '',
      discharge_treatment_level: '',
      third_party_type: '',
      is_produced_water: false,
      recycling_type: '',
      reuse_purpose: '',
      input_volume: '',
      output_volume: '',
    });
    // Clear attachments when form is reset
    setAttachments([]);
  };

  const handleSubmitEntry = async () => {
    setSubmitting(true);
    try {
      // Parse year and month from reporting_period
      const [year, month] = formData.reporting_period.split('-').map(Number);

      // For Recycled category, use output_volume as amount
      const amount = formData.category === 'Recycled'
        ? parseFloat(formData.output_volume) || 0
        : parseFloat(formData.amount) || 0;

      const newEntry = await waterService.createEntry({
        category: formData.category as WaterCategory,
        source: formData.source ? formData.source as WaterSource : undefined,
        amount,
        unit: formData.category === 'Recycled' ? 'm³' : formData.unit as 'm³' | 'ML' | 'kL',
        facility_id: formData.facility_id,
        year,
        month,
        reporting_period: formData.reporting_period,
        stressed_area: formData.stressed_area,
        notes: formData.notes || undefined,
        // GRI 303 fields
        water_quality_type: formData.water_quality_type,
        tds_mg_per_liter: formData.tds_mg_per_liter ? parseFloat(formData.tds_mg_per_liter) : undefined,
        discharge_destination: formData.discharge_destination || undefined,
        discharge_treatment_level: formData.discharge_treatment_level || undefined,
        // CDP Water Security fields
        third_party_type: formData.third_party_type || undefined,
        is_produced_water: formData.is_produced_water,
        // CDP W1.2d: Water Recycling fields
        recycling_type: formData.recycling_type || undefined,
        reuse_purpose: formData.reuse_purpose || undefined,
        input_volume: formData.input_volume ? parseFloat(formData.input_volume) : undefined,
        output_volume: formData.output_volume ? parseFloat(formData.output_volume) : undefined,
      });

      // Link uploaded attachments to the new entry
      if (attachments.length > 0 && newEntry.id) {
        await Promise.all(
          attachments.map(attachment =>
            waterAttachmentService.linkAttachmentToEntry(attachment.id, newEntry.id)
          )
        );
      }

      setEntryDialogOpen(false);
      resetFormData();
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  const getFilteredData = (data: WaterEntry[]): WaterEntry[] => {
    let filtered = data;

    // Permission-based filtering
    if (permissions.userRole === 'Admin') {
      // Admin sees all
    } else if (permissions.userRole === 'Contributor') {
      filtered = filtered.filter(item => item.created_by === user?.email || item.status === 'approved');
    } else {
      filtered = filtered.filter(item => item.status === 'approved');
    }

    // Facility filtering
    if (facilityFilter !== 'all') {
      filtered = filtered.filter(item => item.facility_id === facilityFilter);
    }

    return filtered;
  };

  // Filter by category based on active tab
  const getCategoryForTab = (tab: number): string => {
    switch (tab) {
      case 0: return 'Withdrawal';
      case 1: return 'Discharge';
      case 2: return 'Recycled';
      default: return 'Withdrawal';
    }
  };

  const filteredData = getFilteredData(waterData);
  const tabFilteredData = filteredData.filter(e => e.category === getCategoryForTab(activeTab));

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCurrentPageData = (data: WaterEntry[]) => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Helper to extract month from reporting_period (e.g., "2024-03" -> 3)
  const getMonthFromPeriod = (period: string): number => {
    const parts = period.split('-');
    return parts.length > 1 ? parseInt(parts[1], 10) : 0;
  };

  // Generate chart data from entries - only approved data
  const generateChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Only use approved entries for the chart, filtered by chart facility filter
    let approvedData = waterData.filter(e => e.status === 'approved');

    // Apply chart facility filter
    if (chartFacilityFilter !== 'all') {
      approvedData = approvedData.filter(e => e.facility_id === chartFacilityFilter);
    }
    const chartData = months.map((month, index) => {
      const monthNum = index + 1;
      const withdrawalEntries = approvedData.filter(e => e.category === 'Withdrawal' && getMonthFromPeriod(e.reporting_period) === monthNum);
      const dischargeEntries = approvedData.filter(e => e.category === 'Discharge' && getMonthFromPeriod(e.reporting_period) === monthNum);
      const recycledEntries = approvedData.filter(e => e.category === 'Recycled' && getMonthFromPeriod(e.reporting_period) === monthNum);

      // Use amount_m3 for normalized values, fallback to amount
      const withdrawal = withdrawalEntries.reduce((sum, e) => sum + (e.amount_m3 || e.amount), 0);
      const discharge = dischargeEntries.reduce((sum, e) => sum + (e.amount_m3 || e.amount), 0);
      const recycled = recycledEntries.reduce((sum, e) => sum + (e.amount_m3 || e.amount), 0);

      return {
        month,
        Withdrawal: withdrawal,
        Discharge: discharge,
        Consumption: withdrawal - discharge,
        Recycled: recycled,
      };
    });
    return chartData;
  };

  const chartData = generateChartData();
  const totalCount = filteredData.length;

  if (loading && waterData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
          Water Management
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#dbeafe', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <WaterDrop sx={{ color: '#2563eb' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Withdrawal
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {summary.totalWithdrawal.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>m³</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#fef3c7', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <WaterDrop sx={{ color: '#d97706' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Discharge
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {summary.totalDischarge.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>m³</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#f3e8ff', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <WaterDrop sx={{ color: '#7c3aed' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Consumption
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {summary.totalConsumption.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>m³ (net)</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#d1fae5', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <CheckCircle sx={{ color: '#059669' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Data Status
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {summary.approvedCount}/{totalCount}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
                  {summary.pendingCount > 0 ? `${summary.pendingCount} pending` : 'approved'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Water Trend Chart */}
      <Card sx={{ p: 3, mb: 3, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
            Water Usage Trends
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={chartFacilityFilter}
              onChange={(e) => setChartFacilityFilter(e.target.value)}
              displayEmpty
              sx={{
                fontSize: '0.75rem',
                '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 1 },
                backgroundColor: '#f9fafb',
                '&:hover': { backgroundColor: '#f3f4f6' },
              }}
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <WaterDrop sx={{ fontSize: 14, color: '#6b7280' }} />
                  <span>{value === 'all' ? 'All Facilities' : facilities.find(f => f.id === value)?.name || 'All Facilities'}</span>
                </Box>
              )}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Facilities</MenuItem>
              {facilities.map(f => (
                <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.75rem' }}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorWithdrawal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorDischarge" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#d97706" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#d97706" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorConsumption" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRecycled" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value} />
              <RechartsTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload) return null;
                  const orderedKeys = ['Withdrawal', 'Consumption', 'Discharge', 'Recycled'];
                  const colors: Record<string, string> = { Withdrawal: '#2563eb', Consumption: '#7c3aed', Discharge: '#d97706', Recycled: '#10b981' };
                  const dataMap = Object.fromEntries(payload.map((p: any) => [p.dataKey, p.value]));
                  return (
                    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '8px 12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>{label}</div>
                      {orderedKeys.map(key => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '12px', marginBottom: '2px' }}>
                          <span style={{ color: colors[key] }}>{key}</span>
                          <span style={{ color: '#374151' }}>{typeof dataMap[key] === 'number' ? dataMap[key].toLocaleString() : dataMap[key]} m³</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }}
                iconSize={12}
                content={({ payload }) => {
                  const orderedItems = [
                    { name: 'Withdrawal', color: '#2563eb' },
                    { name: 'Consumption', color: '#7c3aed' },
                    { name: 'Discharge', color: '#d97706' },
                    { name: 'Recycled', color: '#10b981' },
                  ];
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
                      {orderedItems.map((item) => (
                        <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color, display: 'inline-block' }} />
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Area type="monotone" dataKey="Withdrawal" stroke="#2563eb" fillOpacity={1} fill="url(#colorWithdrawal)" strokeWidth={2} />
              <Area type="monotone" dataKey="Consumption" stroke="#7c3aed" fillOpacity={1} fill="url(#colorConsumption)" strokeWidth={2} />
              <Area type="monotone" dataKey="Discharge" stroke="#d97706" fillOpacity={1} fill="url(#colorDischarge)" strokeWidth={2} />
              <Area type="monotone" dataKey="Recycled" stroke="#10b981" fillOpacity={1} fill="url(#colorRecycled)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Card>

      {/* Main Content Card with Tabs */}
      <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
        {/* Tabs & Actions Header */}
        <Box sx={{
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#6b7280',
                minHeight: 48,
                px: 2,
              },
              '& .Mui-selected': {
                color: '#111827',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#2563eb',
                height: 2,
              },
            }}
          >
            <Tab label="Withdrawal" />
            <Tab label="Discharge" />
            <Tab label="Recycled" />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {permissions.canCreate && (
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 18 }} />}
                onClick={() => {
                  const category = getCategoryForTab(activeTab);
                  setFormData(prev => ({ ...prev, category }));
                  setEntryDialogOpen(true);
                }}
                sx={{
                  backgroundColor: '#2563eb',
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' },
                }}
              >
                Add Entry
              </Button>
            )}
            {/* Export Button - Admin Only */}
            {permissions.userRole === 'Admin' && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: 16 }} />}
                  disabled={tabFilteredData.length === 0 || exporting}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  sx={{
                    borderColor: '#e5e7eb',
                    color: '#6b7280',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' },
                    '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' },
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={() => setExportMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Filter Bar */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Facility</InputLabel>
            <Select
              value={facilityFilter}
              label="Facility"
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setPage(0);
              }}
              sx={{ backgroundColor: '#fff', fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              {facilityOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {facilityFilter !== 'all' && (
            <Button
              size="small"
              onClick={() => {
                setFacilityFilter('all');
                setPage(0);
              }}
              sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
            >
              Clear Filter
            </Button>
          )}

          <Typography sx={{ color: '#9ca3af', ml: 'auto', fontSize: '0.75rem' }}>
            {tabFilteredData.length} entries
          </Typography>
        </Box>

        {/* Data Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Source</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Amount</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Facility</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem', width: 90 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tabFilteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                    <WaterDrop sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: '#6b7280', mb: 0.5 }}>No water data yet</Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>Click "Add Entry" to start tracking water usage</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getCurrentPageData(tabFilteredData).map((row) => {
                  const status = getStatusConfig(row.status);
                  const actions = permissions.getEntryActions(row.status, row.created_by);

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f9fafb' },
                        '& td': { py: 2, borderBottom: '1px solid #f3f4f6' }
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={row.category}
                          size="small"
                          sx={{
                            backgroundColor: row.category === 'Withdrawal' ? '#dbeafe' : row.category === 'Discharge' ? '#fef3c7' : row.category === 'Recycled' ? '#d1fae5' : '#f3e8ff',
                            color: row.category === 'Withdrawal' ? '#2563eb' : row.category === 'Discharge' ? '#d97706' : row.category === 'Recycled' ? '#059669' : '#7c3aed',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151' }}>
                          {row.source}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {row.amount.toLocaleString()} {row.unit}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.reporting_period ? (
                            (() => {
                              const [year, month] = row.reporting_period.split('-');
                              return month && year
                                ? `${new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'short' })} ${year}`
                                : row.reporting_period;
                            })()
                          ) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.facility_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.created_by ? row.created_by.split('@')[0].split('.').map((n: string) => n.charAt(0).toUpperCase() + n.slice(1)).join(' ') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 24,
                            backgroundColor: status.bg,
                            color: status.color,
                            fontWeight: 500,
                            '& .MuiChip-icon': { color: status.color },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {/* Edit button - Admin only */}
                          {permissions.userRole === 'Admin' && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(row)}
                                sx={{ color: '#2563eb' }}
                              >
                                <Edit sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canApprove && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }}
                                sx={{ color: '#059669' }}
                              >
                                <ThumbUp sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row.id)}
                                sx={{ color: '#dc2626' }}
                              >
                                <Delete sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!actions.canApprove && !actions.canDelete && permissions.userRole !== 'Admin' && (
                            <Tooltip title="View">
                              <IconButton size="small" sx={{ color: '#9ca3af' }}>
                                <Visibility sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={tabFilteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            borderTop: '1px solid #e5e7eb',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
              color: '#6b7280',
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.8rem',
            },
          }}
        />
      </Card>

      {/* Manual Entry Dialog */}
      <Dialog
        open={entryDialogOpen}
        onClose={() => {
          setEntryDialogOpen(false);
          setEditMode(false);
          setEditingEntry(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', maxHeight: '85vh' } }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          backgroundColor: editMode ? '#fef3c7' : '#dbeafe',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
              {editMode ? 'Edit Water Entry' : 'Add Water Entry'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
              {editMode ? 'Modify existing water entry data' : 'Track water withdrawal, discharge, and consumption'}
            </Typography>
          </Box>
          <IconButton onClick={() => {
            setEntryDialogOpen(false);
            setEditMode(false);
            setEditingEntry(null);
          }} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Facility Selection */}
            <FormControl fullWidth size="small">
              <InputLabel shrink sx={{ fontSize: '0.8rem' }}>Facility</InputLabel>
              <Select
                value={formData.facility_id}
                label="Facility"
                onChange={(e) => setFormData({ ...formData, facility_id: e.target.value })}
                sx={{ fontSize: '0.8rem' }}
                displayEmpty
                notched
                renderValue={(value) => {
                  if (value === '') {
                    return 'Organization-wide';
                  }
                  const facility = facilities.find(f => f.id === value);
                  return facility?.name || value;
                }}
              >
                {/* Organization-wide option */}
                <MenuItem value="" sx={{ fontSize: '0.8rem', py: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: '#6b7280' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>Organization-wide</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Not facility-specific</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {facilities.length === 0 ? (
                  <MenuItem disabled value="no-facilities">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      No facilities available
                    </Typography>
                  </MenuItem>
                ) : (
                  facilities.map((f) => (
                    <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                          {f.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {f.type} • {f.country}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Reporting Period */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Year</InputLabel>
                <Select
                  value={formData.reporting_period.split('-')[0] || new Date().getFullYear().toString()}
                  label="Year"
                  onChange={(e) => {
                    const month = formData.reporting_period.split('-')[1] || '01';
                    setFormData({ ...formData, reporting_period: `${e.target.value}-${month}` });
                  }}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <MenuItem key={year} value={year.toString()} sx={{ fontSize: '0.8rem', py: 0.5 }}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Month</InputLabel>
                <Select
                  value={parseInt(formData.reporting_period.split('-')[1] || '1', 10)}
                  label="Month"
                  onChange={(e) => {
                    const year = formData.reporting_period.split('-')[0] || new Date().getFullYear().toString();
                    const month = String(e.target.value).padStart(2, '0');
                    setFormData({ ...formData, reporting_period: `${year}-${month}` });
                  }}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[
                    { value: 1, label: 'January' },
                    { value: 2, label: 'February' },
                    { value: 3, label: 'March' },
                    { value: 4, label: 'April' },
                    { value: 5, label: 'May' },
                    { value: 6, label: 'June' },
                    { value: 7, label: 'July' },
                    { value: 8, label: 'August' },
                    { value: 9, label: 'September' },
                    { value: 10, label: 'October' },
                    { value: 11, label: 'November' },
                    { value: 12, label: 'December' },
                  ].map((month) => (
                    <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Category Selection */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value, source: '' })}
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value="Withdrawal" sx={{ fontSize: '0.8rem', py: 0.5 }}>Withdrawal</MenuItem>
                <MenuItem value="Discharge" sx={{ fontSize: '0.8rem', py: 0.5 }}>Discharge</MenuItem>
                <MenuItem value="Recycled" sx={{ fontSize: '0.8rem', py: 0.5 }}>Recycled</MenuItem>
              </Select>
            </FormControl>

            {/* Source Selection - Only shown for Withdrawal category */}
            {formData.category === 'Withdrawal' && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Source</InputLabel>
                <Select
                  value={formData.source}
                  label="Source"
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {WATER_SOURCES[formData.category]?.map((src) => (
                    <MenuItem key={src} value={src} sx={{ fontSize: '0.8rem', py: 0.5 }}>{src}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Amount and Unit - Hidden for Recycled category (uses output_volume instead) */}
            {formData.category !== 'Recycled' && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  fullWidth
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                  InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                  placeholder="Enter quantity"
                />
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Unit</InputLabel>
                  <Select
                    value={formData.unit}
                    label="Unit"
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    <MenuItem value="m³" sx={{ fontSize: '0.8rem', py: 0.5 }}>m³</MenuItem>
                    <MenuItem value="kL" sx={{ fontSize: '0.8rem', py: 0.5 }}>kL</MenuItem>
                    <MenuItem value="ML" sx={{ fontSize: '0.8rem', py: 0.5 }}>ML</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Water Stressed Area */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.stressed_area}
                  onChange={(e) => setFormData({ ...formData, stressed_area: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Water stressed area</Typography>}
            />

            {/* GRI 303: Water Quality Classification */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Water Quality</InputLabel>
                <Select
                  value={formData.water_quality_type}
                  label="Water Quality"
                  onChange={(e) => setFormData({ ...formData, water_quality_type: e.target.value as WaterQualityType })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="freshwater" sx={{ fontSize: '0.8rem', py: 0.5 }}>Freshwater (≤1000 mg/L TDS)</MenuItem>
                  <MenuItem value="other_water" sx={{ fontSize: '0.8rem', py: 0.5 }}>Other Water (&gt;1000 mg/L TDS)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="TDS (mg/L)"
                type="number"
                size="small"
                fullWidth
                value={formData.tds_mg_per_liter}
                onChange={(e) => {
                  const tds = e.target.value;
                  const quality = tds && parseFloat(tds) > 1000 ? 'other_water' : 'freshwater';
                  setFormData({ ...formData, tds_mg_per_liter: tds, water_quality_type: quality as WaterQualityType });
                }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                placeholder="Optional"
              />
            </Box>

            {/* GRI 303-4: Discharge-specific fields (shown only for Discharge category) */}
            {formData.category === 'Discharge' && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <FormControl fullWidth required size="small">
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Discharge Destination</InputLabel>
                  <Select
                    value={formData.discharge_destination}
                    label="Discharge Destination"
                    onChange={(e) => setFormData({ ...formData, discharge_destination: e.target.value as DischargeDestination })}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {DISCHARGE_DESTINATIONS.map((dest) => (
                      <MenuItem key={dest.value} value={dest.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{dest.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Treatment Level</InputLabel>
                  <Select
                    value={formData.discharge_treatment_level}
                    label="Treatment Level"
                    onChange={(e) => setFormData({ ...formData, discharge_treatment_level: e.target.value as DischargeTreatmentLevel })}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {TREATMENT_LEVELS.map((level) => (
                      <MenuItem key={level.value} value={level.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{level.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* CDP: Third-party water type (shown only when source is Third-party Water) */}
            {formData.source === 'Third-party Water' && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Third-party Type</InputLabel>
                <Select
                  value={formData.third_party_type}
                  label="Third-party Type"
                  onChange={(e) => setFormData({ ...formData, third_party_type: e.target.value as ThirdPartyWaterType })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {THIRD_PARTY_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* CDP: Produced/Process water flag */}
            {formData.source === 'Produced/Process Water' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_produced_water}
                    onChange={(e) => setFormData({ ...formData, is_produced_water: e.target.checked })}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>This is produced/process water (CDP W1.2)</Typography>}
              />
            )}

            {/* CDP W1.2d: Recycling-specific fields (shown only for Recycled category) */}
            {formData.category === 'Recycled' && (
              <>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <FormControl fullWidth required size="small">
                    <InputLabel sx={{ fontSize: '0.8rem' }}>Recycling Type</InputLabel>
                    <Select
                      value={formData.recycling_type}
                      label="Recycling Type"
                      onChange={(e) => setFormData({ ...formData, recycling_type: e.target.value as RecyclingType })}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {RECYCLING_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{type.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required size="small">
                    <InputLabel sx={{ fontSize: '0.8rem' }}>Reuse Purpose</InputLabel>
                    <Select
                      value={formData.reuse_purpose}
                      label="Reuse Purpose"
                      onChange={(e) => setFormData({ ...formData, reuse_purpose: e.target.value as ReusePurpose })}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {REUSE_PURPOSES.map((purpose) => (
                        <MenuItem key={purpose.value} value={purpose.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{purpose.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {/* ETP Input/Output Volume Tracking */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="Input Volume (m³)"
                    type="number"
                    size="small"
                    fullWidth
                    value={formData.input_volume}
                    onChange={(e) => setFormData({ ...formData, input_volume: e.target.value })}
                    InputProps={{ sx: { fontSize: '0.8rem' } }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    placeholder="Water into treatment"
                    helperText="Volume entering ETP/treatment system"
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.5 } }}
                  />
                  <TextField
                    label="Output Volume (m³)"
                    type="number"
                    size="small"
                    fullWidth
                    value={formData.output_volume}
                    onChange={(e) => setFormData({ ...formData, output_volume: e.target.value })}
                    InputProps={{ sx: { fontSize: '0.8rem' } }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    placeholder="Recycled water output"
                    helperText="Recycled water for reuse"
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.5 } }}
                  />
                </Box>
                {/* Efficiency display */}
                {formData.input_volume && formData.output_volume && parseFloat(formData.input_volume) > 0 && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f0fdf4',
                    borderRadius: 2,
                    border: '1px solid #bbf7d0'
                  }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 18 }} />
                    <Typography sx={{ fontSize: '0.8rem', color: '#166534' }}>
                      Treatment Efficiency: <strong>{((parseFloat(formData.output_volume) / parseFloat(formData.input_volume)) * 100).toFixed(1)}%</strong>
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              InputProps={{ sx: { fontSize: '0.8rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
              placeholder="Add any relevant notes..."
            />

            {/* Evidence Attachments */}
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <AttachFile sx={{ fontSize: 16, color: '#6b7280' }} />
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontSize: '0.8rem' }}>
                  Supporting Evidence (Optional)
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem', display: 'block', mb: 1 }}>
                Attach water bills, meter readings, discharge permits, or other supporting documents
              </Typography>
              <FileUploadZone
                files={attachments}
                onFilesChange={setAttachments}
                onUpload={handleFileUpload}
                onRemove={handleFileRemove}
                maxFiles={10}
                maxFileSize={50 * 1024 * 1024}
                maxTotalSize={200 * 1024 * 1024}
              />
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            size="small"
            onClick={() => {
              setEntryDialogOpen(false);
              setEditMode(false);
              setEditingEntry(null);
            }}
            sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={editMode ? handleUpdateEntry : handleSubmitEntry}
            disabled={submitting || !formData.category || (formData.category === 'Withdrawal' && !formData.source) || (formData.category === 'Recycled' ? !formData.output_volume : !formData.amount)}
            sx={{
              backgroundColor: editMode ? '#d97706' : '#10b981',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              boxShadow: 'none',
              '&:hover': { backgroundColor: editMode ? '#b45309' : '#059669', boxShadow: 'none' },
              '&:disabled': { backgroundColor: '#e5e7eb' },
            }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : editMode ? 'Update Entry' : 'Submit for Approval'}
          </Button>
        </Box>
      </Dialog>

      {/* Approval Dialog */}
      {selectedEntry && (
        <ApprovalDialog
          open={approvalDialogOpen}
          onClose={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onApprove={(comment) => { handleApprove(selectedEntry.id, comment); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onReject={(reason) => { handleReject(selectedEntry.id, reason); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          entryData={{
            category: selectedEntry.category,
            value: selectedEntry.amount,
            unit: selectedEntry.unit,
            uploadedBy: selectedEntry.created_by,
            uploadedAt: selectedEntry.created_at,
          }}
        />
      )}

      {/* GRI 303 & CDP Water Security Metrics Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
          GRI 303 & CDP Water Security Metrics
        </Typography>

        {metricsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* GRI 303-3: Water Withdrawal */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    backgroundColor: '#dbeafe', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <WaterDrop sx={{ color: '#2563eb', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                      GRI 303-3: Water Withdrawal
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      Breakdown by source and quality
                    </Typography>
                  </Box>
                </Box>

                {gri303Data ? (
                  <>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, backgroundColor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#166534', fontWeight: 500 }}>Freshwater</Typography>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d' }}>
                            {gri303Data.withdrawal_freshwater_total.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>m³</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, backgroundColor: '#fef3c7', borderRadius: 2, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 500 }}>Other Water</Typography>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#d97706' }}>
                            {gri303Data.withdrawal_other_water_total.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>m³</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {gri303Data.withdrawal_by_source.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', mb: 1 }}>By Source</Typography>
                        {gri303Data.withdrawal_by_source.map((src, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f3f4f6' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: '#374151' }}>{src.source}</Typography>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#111827' }}>{src.total_volume.toLocaleString()} m³</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                    No withdrawal data available
                  </Typography>
                )}
              </Card>
            </Grid>

            {/* GRI 303-4: Water Discharge */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    backgroundColor: '#f3e8ff', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <WaterDrop sx={{ color: '#7c3aed', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                      GRI 303-4: Water Discharge
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      By destination and treatment
                    </Typography>
                  </Box>
                </Box>

                {gri303Data ? (
                  <>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, backgroundColor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#166534', fontWeight: 500 }}>Freshwater</Typography>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d' }}>
                            {gri303Data.discharge_freshwater_total.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>m³</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, backgroundColor: '#fef3c7', borderRadius: 2, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 500 }}>Other Water</Typography>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#d97706' }}>
                            {gri303Data.discharge_other_water_total.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>m³</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {gri303Data.discharge_by_destination.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', mb: 1 }}>By Destination</Typography>
                        {gri303Data.discharge_by_destination.map((dest, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f3f4f6' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: '#374151', textTransform: 'capitalize' }}>
                              {dest.destination.replace(/_/g, ' ')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#111827' }}>{dest.total_volume.toLocaleString()} m³</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                    No discharge data available
                  </Typography>
                )}
              </Card>
            </Grid>

            {/* GRI 303-5: Water Consumption (Auto-calculated) */}
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    backgroundColor: '#d1fae5', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <CheckCircle sx={{ color: '#059669', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                      GRI 303-5: Consumption
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      Auto-calculated
                    </Typography>
                  </Box>
                </Box>

                {gri303Data ? (
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: '#059669' }}>
                      {gri303Data.total_consumption.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>m³</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', mt: 1 }}>
                      = Withdrawal - Discharge
                    </Typography>
                    {gri303Data.consumption_stressed_total > 0 && (
                      <Chip
                        label={`${gri303Data.consumption_stressed_total.toLocaleString()} m³ in stressed areas`}
                        size="small"
                        sx={{ mt: 1.5, fontSize: '0.65rem', backgroundColor: '#fef2f2', color: '#dc2626' }}
                      />
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                    No data available
                  </Typography>
                )}
              </Card>
            </Grid>

            {/* CDP W1.2b: Water Intensity */}
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    backgroundColor: '#eff6ff', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <WaterDrop sx={{ color: '#3b82f6', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                      CDP W1.2b: Intensity
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      Auto-calculated metrics
                    </Typography>
                  </Box>
                </Box>

                {intensityData ? (
                  <Box>
                    {/* Show all intensity metrics in a list */}
                    {intensityData.intensity_metrics.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {intensityData.intensity_metrics.map((metric, idx) => (
                          <Box key={idx} sx={{ p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#2563eb' }}>
                                {metric.intensity_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                {metric.numerator_unit} per {metric.denominator_unit}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', mt: 0.5 }}>
                              {metric.metric_type === 'per_area' && 'Floor Area Intensity'}
                              {metric.metric_type === 'per_employee' && 'Per Employee Usage'}
                              {metric.metric_type === 'per_revenue' && 'Revenue Intensity'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', py: 1 }}>
                        Add employee count or floor area to facilities to calculate more intensity metrics
                      </Typography>
                    )}

                    {/* Water Stress Summary - GRI 303 & CDP W2 */}
                    {gri303Data && (
                      <Box sx={{ mt: 2, p: 1.5, backgroundColor: gri303Data.withdrawal_stressed_total > 0 || gri303Data.discharge_stressed_total > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: 2, border: `1px solid ${gri303Data.withdrawal_stressed_total > 0 || gri303Data.discharge_stressed_total > 0 ? '#fecaca' : '#bbf7d0'}` }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: gri303Data.withdrawal_stressed_total > 0 || gri303Data.discharge_stressed_total > 0 ? '#dc2626' : '#166534', mb: 0.5 }}>
                          Water Stress Areas (GRI 303 / CDP W2)
                        </Typography>
                        {gri303Data.withdrawal_stressed_total > 0 || gri303Data.discharge_stressed_total > 0 ? (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#7f1d1d' }}>
                              Withdrawal: <strong>{gri303Data.withdrawal_stressed_total.toLocaleString()}</strong> m³
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#7f1d1d' }}>
                              Discharge: <strong>{gri303Data.discharge_stressed_total.toLocaleString()}</strong> m³
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.7rem', color: '#166534' }}>
                            No operations in water-stressed areas
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                    No intensity data available
                  </Typography>
                )}
              </Card>
            </Grid>

            {/* CDP W1.2d: Recycling Rate */}
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 2,
                    backgroundColor: '#ecfdf5', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                      CDP W1.2d: Recycling
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      Recycling & reuse metrics
                    </Typography>
                  </Box>
                </Box>

                {intensityData ? (
                  <Box>
                    {/* Metric selector and value */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                          <Typography sx={{
                            fontSize: '1.75rem',
                            fontWeight: 700,
                            color: recyclingMetricType === 'ratio' && (intensityData.recycling_metrics.recycling_ratio_percent ?? 0) > 100
                              ? '#f59e0b'
                              : '#10b981'
                          }}>
                            {recyclingMetricType === 'rate'
                              ? intensityData.recycling_metrics.recycling_rate_percent.toFixed(1)
                              : (intensityData.recycling_metrics.recycling_ratio_percent ?? intensityData.recycling_metrics.recycling_rate_percent).toFixed(1)
                            }%
                          </Typography>
                          <Select
                            size="small"
                            value={recyclingMetricType}
                            onChange={(e) => setRecyclingMetricType(e.target.value as 'rate' | 'ratio')}
                            sx={{
                              fontSize: '0.7rem',
                              height: 28,
                              '& .MuiSelect-select': { py: 0.5, px: 1 },
                              backgroundColor: '#f9fafb',
                              borderRadius: 1,
                            }}
                          >
                            <MenuItem value="rate" sx={{ fontSize: '0.75rem' }}>Rate (max 100%)</MenuItem>
                            <MenuItem value="ratio" sx={{ fontSize: '0.75rem' }}>Ratio (multi-pass)</MenuItem>
                          </Select>
                        </Box>
                        <Box sx={{ height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                          <Box sx={{
                            width: `${Math.min(
                              recyclingMetricType === 'rate'
                                ? intensityData.recycling_metrics.recycling_rate_percent
                                : (intensityData.recycling_metrics.recycling_ratio_percent ?? intensityData.recycling_metrics.recycling_rate_percent),
                              100
                            )}%`,
                            height: '100%',
                            backgroundColor: recyclingMetricType === 'ratio' && (intensityData.recycling_metrics.recycling_ratio_percent ?? 0) > 100
                              ? '#f59e0b'
                              : '#10b981',
                            borderRadius: 3,
                          }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', mt: 0.5 }}>
                          {intensityData.recycling_metrics.total_recycled.toLocaleString()} m³ recycled
                        </Typography>
                      </Box>
                    </Box>

                    {/* Compact breakdown grid */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5, pt: 1.5, borderTop: '1px solid #f3f4f6' }}>
                      {/* By Type */}
                      {intensityData.recycling_metrics.by_type && intensityData.recycling_metrics.by_type.length > 0 && (
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#6b7280', mb: 0.5 }}>By Type</Typography>
                          {intensityData.recycling_metrics.by_type.map((item, idx) => (
                            <Typography key={idx} sx={{ fontSize: '0.7rem', color: '#374151' }}>
                              {item.recycling_type === 'internal' ? 'Internal' : 'External'}: <strong>{item.volume.toLocaleString()}</strong> m³
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {/* By Purpose */}
                      {intensityData.recycling_metrics.by_purpose && intensityData.recycling_metrics.by_purpose.length > 0 && (
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#6b7280', mb: 0.5 }}>By Purpose</Typography>
                          {intensityData.recycling_metrics.by_purpose.map((item, idx) => (
                            <Typography key={idx} sx={{ fontSize: '0.7rem', color: '#374151', textTransform: 'capitalize' }}>
                              {item.purpose}: <strong>{item.volume.toLocaleString()}</strong> m³
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Treatment Efficiency - Compact inline */}
                    {intensityData.recycling_metrics.total_input_volume > 0 && (
                      <Box sx={{
                        mt: 1.5, p: 1.5,
                        backgroundColor: '#f0fdf4',
                        borderRadius: 2,
                        border: '1px solid #bbf7d0'
                      }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#166534', mb: 0.5 }}>
                          ETP Treatment
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography sx={{ fontSize: '0.7rem', color: '#374151' }}>
                            In: <strong>{intensityData.recycling_metrics.total_input_volume.toLocaleString()}</strong> →
                            Out: <strong>{intensityData.recycling_metrics.total_output_volume.toLocaleString()}</strong> m³
                          </Typography>
                          {intensityData.recycling_metrics.average_efficiency_percent !== undefined && (
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#10b981' }}>
                              {intensityData.recycling_metrics.average_efficiency_percent.toFixed(0)}%
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                    No recycling data available
                  </Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};
